<script setup lang="ts">
import type {
  BtnPriority,
  BtnSize,
  BtnVariant,
} from '@/components/btns/KBtn.vue';

import { ref } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';

export interface SplitBtnItem {
  title: string;
  value: string;
  prependIcon?: string;
  disabled?: boolean;
}

export interface Props {
  label?: string;
  items?: SplitBtnItem[];
  priority?: BtnPriority;
  variant?: BtnVariant;
  size?: BtnSize;
  loading?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  items: () => [],
  priority: 'primary',
  variant: 'flat',
  size: 'md',
  loading: false,
  disabled: false,
});

const emit = defineEmits<{
  click: [event: Event];
  select: [value: string, event: Event];
}>();

const isMenuOpen = ref(false);

function handleItemClick(event: Event, item: SplitBtnItem): void {
  isMenuOpen.value = false;
  emit('select', item.value, event);
}
</script>

<template>
  <div :class="$style.kSplitBtn" data-testid="kSplitBtn">
    <KBtn
      :priority="props.priority"
      :variant="props.variant"
      :size="props.size"
      :loading="props.loading"
      :disabled="props.disabled"
      :class="$style.mainBtn"
      @click="emit('click', $event)"
    >
      <slot>{{ props.label }}</slot>
    </KBtn>
    <v-menu v-model="isMenuOpen" location="top end">
      <template #activator="{ props: menuProps }">
        <KBtn
          v-bind="menuProps"
          :priority="props.priority"
          :variant="props.variant"
          :size="props.size"
          :disabled="props.disabled || props.loading"
          icon="mdi-chevron-down"
          :class="$style.toggleBtn"
        />
      </template>
      <v-list density="compact" :class="$style.menuList">
        <v-list-item
          v-for="item in props.items"
          :key="item.value"
          :prepend-icon="item.prependIcon"
          :title="item.title"
          :disabled="item.disabled"
          @click="handleItemClick($event, item)"
        />
      </v-list>
    </v-menu>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.kSplitBtn {
  display: flex;
  align-items: stretch;
}

.mainBtn {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.toggleBtn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 1px solid rgba(colors.$color-white, 0.4);
}

.menuList {
  :global {
    .v-list-item__spacer {
      width: 8px !important;
    }
  }
}
</style>
